import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useWizardState } from "../hooks/useWizardState";
import { Input } from "../components/ui/Form/Input";
import { Listbox } from "../components/shared/form/Listbox";
import { DatePicker } from "../components/shared/form/Datepicker";
import { Switch } from "../components/ui/Form/Switch";
import { MapLocationIcon } from "../components/icons/MapLocationIcon";
import { loadGoogleMapsScript, getPlacePredictions } from "../services/places";
import "./Step1Essentials.css";

interface Airport {
  id: number;
  name: string;
  value: string;
}

const getAirports = (t: (key: string) => string): Airport[] => [
  { id: 1, name: t("options.airports.lisbon"), value: "lisbon" },
  { id: 2, name: t("options.airports.porto"), value: "porto" },
  { id: 3, name: t("options.airports.faro"), value: "faro" },
  { id: 4, name: t("options.airports.other"), value: "other" },
];

export const Step1Essentials: React.FC = () => {
  const { t } = useTranslation();
  const { formData, updateFormData, errors, setFieldError } = useWizardState();
  const [destinationSuggestions, setDestinationSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showOtherAirport, setShowOtherAirport] = useState(formData.departureAirport === "other");
  const [apiReady, setApiReady] = useState(false);
  
  const AIRPORTS = useMemo(() => getAirports(t), [t]);

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
    if (apiKey && apiKey !== "your_google_places_api_key_here") {
      loadGoogleMapsScript(apiKey)
        .then(() => {
          setApiReady(true);
          console.log("Google Places API (New) ready");
        })
        .catch((error) => {
          console.error("Failed to initialize Places API:", error);
          setApiReady(false);
        });
    } else {
      console.warn("Google Places API key not configured. Autocomplete will not work.");
      setApiReady(false);
    }
  }, []);

  // Find the selected airport object based on formData.departureAirport
  const selectedAirport = useMemo(() => {
    if (!formData.departureAirport) return null;
    return AIRPORTS.find((airport) => airport.value === formData.departureAirport) || null;
  }, [formData.departureAirport, AIRPORTS]);

  useEffect(() => {
    setShowOtherAirport(formData.departureAirport === "other");
  }, [formData.departureAirport]);

  const handleAirportChange = (airport: Airport | null) => {
    if (airport) {
      updateFormData({ 
        departureAirport: airport.value, 
        otherAirport: airport.value !== "other" ? "" : formData.otherAirport 
      });
      setFieldError("departureAirport", undefined);
    } else {
      updateFormData({ departureAirport: "" });
    }
  };

  const handleDestinationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    updateFormData({ destination: value });
    setFieldError("destination", undefined);

    if (apiReady && value.length >= 2) {
      getPlacePredictions(value, (predictions) => {
        if (predictions && predictions.length > 0) {
          setDestinationSuggestions(predictions.map((p) => p.description));
          setShowSuggestions(true);
        } else {
          setShowSuggestions(false);
        }
      });
    } else {
      setShowSuggestions(false);
    }
  };

  const handleDestinationSelect = (suggestion: string) => {
    updateFormData({ destination: suggestion });
    setShowSuggestions(false);
  };

  // Parse date string in local time to avoid timezone issues
  const parseDateLocal = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Format date to local date string (YYYY-MM-DD) to avoid timezone issues
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateRangeChange = (selectedDates: Date[]) => {
    if (selectedDates.length === 2) {
      // Use local date formatting instead of ISO to avoid timezone issues on iOS Safari
      const startDate = formatDateLocal(selectedDates[0]);
      const endDate = formatDateLocal(selectedDates[1]);
      const duration = Math.ceil((selectedDates[1].getTime() - selectedDates[0].getTime()) / (1000 * 60 * 60 * 24));
      
      updateFormData({ 
        departureDate: startDate,
        departureDateEnd: endDate,
        duration: duration
      });
    setFieldError("departureDate", undefined);
    } else if (selectedDates.length === 0) {
      updateFormData({ 
        departureDate: "",
        departureDateEnd: "",
        duration: 0
      });
    }
  };

  // Get date range value for DatePicker - convert strings to Date objects in local time
  const dateRangeValue = useMemo(() => {
    if (formData.departureDate && formData.departureDateEnd) {
      // Parse dates in local time to avoid timezone issues on iOS Safari
      return [parseDateLocal(formData.departureDate), parseDateLocal(formData.departureDateEnd)];
    }
    if (formData.departureDate) {
      return [parseDateLocal(formData.departureDate)];
    }
    return undefined;
  }, [formData.departureDate, formData.departureDateEnd]);

  // Format date range for display
  const formattedDateRange = useMemo(() => {
    if (formData.departureDate && formData.departureDateEnd) {
      // Parse dates in local time to avoid timezone shifts
      const startDate = parseDateLocal(formData.departureDate);
      const endDate = parseDateLocal(formData.departureDateEnd);
      
      const formatDate = (date: Date) => {
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric' 
        });
      };
      
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    return null;
  }, [formData.departureDate, formData.departureDateEnd]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="step1-essentials">
      <h2 className="step-heading">{t("step1.heading")}</h2>

      <div className="form-group">
        <div className="destination-wrapper">
          <Input
            id="destination"
            label={t("step1.destination.label")}
            value={formData.destination}
            onChange={handleDestinationChange}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            error={errors.destination}
            required
            autoComplete="off"
            placeholder={t("step1.destination.placeholder")}
            prefix={<MapLocationIcon className="w-4.5 h-4.5" />}
            classNames={{
              input: "!pl-[2.25rem]"
            }}
          />
          {showSuggestions && destinationSuggestions.length > 0 && (
            <div className="destination-suggestions">
              {destinationSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  className="suggestion-item"
                  onClick={() => handleDestinationSelect(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <Listbox
            data={AIRPORTS}
            value={selectedAirport}
            placeholder={t("step1.departureAirport.placeholder")}
            onChange={handleAirportChange}
            label={t("step1.departureAirport.label")}
            displayField="name"
            error={errors.departureAirport}
            required
          />
            </div>

        {showOtherAirport && (
          <div className="conditional-field animate-slide-down">
            <Input
              id="otherAirport"
              label={t("step1.otherAirport.label")}
              value={formData.otherAirport || ""}
              onChange={(e) => {
                updateFormData({ otherAirport: e.target.value });
                setFieldError("otherAirport", undefined);
              }}
              error={errors.otherAirport}
              required
            />
          </div>
        )}

        <div className="form-group">
          <DatePicker
            isCalendar
            label={t("step1.departureDateRange.label")}
            value={dateRangeValue}
            onChange={handleDateRangeChange}
            error={errors.departureDate}
            required
            options={{
              mode: "range",
              dateFormat: "Y-m-d",
              minDate: today,
              parseDate: (datestr: string) => {
                // Parse date string in local time to avoid timezone issues on iOS Safari
                const [year, month, day] = datestr.split('-').map(Number);
                return new Date(year, month - 1, day);
              },
            }}
          />
          {formattedDateRange && (
            <p className="mt-2 text-sm text-gray-600 dark:text-dark-200">
              {t("step1.departureDateRange.selected", { range: formattedDateRange })}
            </p>
          )}
        </div>


        <Switch
          id="flexibleDates"
          label={t("step1.flexibleDates.label")}
          checked={formData.flexibleDates}
          onChange={(e) => {
            updateFormData({ flexibleDates: e.target.checked, flexibilityRange: e.target.checked ? formData.flexibilityRange : "" });
          }}
        />

        {formData.flexibleDates && (
          <div className="conditional-field animate-slide-down">
            <Input
              id="flexibilityRange"
              label={t("step1.flexibility.label")}
              placeholder={t("step1.flexibility.placeholder")}
              value={formData.flexibilityRange || ""}
              onChange={(e) => {
                updateFormData({ flexibilityRange: e.target.value });
                setFieldError("flexibilityRange", undefined);
              }}
              error={errors.flexibilityRange}
              required
            />
          </div>
        )}
      </div>
    </div>
  );
};

